import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Box, Typography, Skeleton } from "@mui/material";
import { RocketLaunchRounded, ArrowForwardRounded } from "@mui/icons-material";
import MethodGet from "../../config/Service";
import FormatCurrency from "../../utils/FormatCurrency";
import AuthContext from "../../Context/auth/AuthContext";
import { useContext } from "react";

const LaunchesBanner = () => {
    const { autenticado } = useContext(AuthContext);
    const [launches, setLaunches] = useState([]);
    const [loading, setLoading] = useState(true);
    const scrollRef = useRef(null);
    const animationRef = useRef(null);
    const [isHovered, setIsHovered] = useState(false);

    useEffect(() => {
        console.log("LaunchesBanner: Fetching...");
        MethodGet("/distribucion/products/launches?per_page=20")
            .then((res) => {
                const data = res.data?.data || res.data || [];
                console.log("LaunchesBanner: Data received", data);
                if (Array.isArray(data)) {
                    setLaunches(data);
                } else {
                    console.error("LaunchesBanner: Data is not an array", data);
                    setLaunches([]);
                }
                setLoading(false);
            })
            .catch((err) => {
                console.error("LaunchesBanner: Error loading", err);
                setLoading(false);
            });
    }, []);

    // Auto-scroll animation
    useEffect(() => {
        if (!scrollRef.current || launches.length < 4) return; // Need at least 4 items for effective scroll

        const container = scrollRef.current;
        let scrollSpeed = 0.4; // Despacito bonito

        const animate = () => {
            if (!isHovered && container) {
                container.scrollLeft += scrollSpeed;
                // Infinite scroll capability
                // If we scrolled past 1/3 (the original set width), reset to 0
                // Note: This relies on the content being exactly triplicated
                if (container.scrollLeft >= container.scrollWidth / 3) {
                    container.scrollLeft = 1; // 1 instead of 0 to avoid jitter
                }
            }
            animationRef.current = requestAnimationFrame(animate);
        };

        animationRef.current = requestAnimationFrame(animate);

        return () => {
            if (animationRef.current) cancelAnimationFrame(animationRef.current);
        };
    }, [launches, isHovered]);

    // Render logic
    const hasLaunches = !loading && launches && launches.length > 0;

    // If there are no launches, do not render the banner
    if (!loading && launches.length === 0) {
        return null;
    }

    // Duplicate products for infinite scroll effect (x3 for smooth loop) ONLY if there are enough products
    const safeLaunches = Array.isArray(launches) ? launches : [];
    const isScrollable = safeLaunches.length >= 4;
    const boxProducts = loading 
        ? Array.from({ length: 6 }) 
        : (isScrollable ? [...safeLaunches, ...safeLaunches, ...safeLaunches] : safeLaunches);

    // console.log(`LaunchesBanner: Reached JSX return. Products: ${boxProducts.length}, Loading: ${loading}`);

    return (
        <Box
            sx={{
                minHeight: 500, // Force height
                py: 6,
                px: { xs: 2, md: 4 },
                // Premium Gradient: Deep Purple to Vibrant Pink
                background: "linear-gradient(135deg, #4A0033 0%, #801F56 40%, #e94560 100%)",
                position: "relative",
                overflow: "hidden",
                boxShadow: "inset 0 0 30px rgba(0,0,0,0.2)",
                "&::before": {
                    content: '""',
                    position: "absolute",
                    top: -100,
                    left: -100,
                    width: 600,
                    height: 600,
                    background: "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
                    filter: "blur(60px)",
                    pointerEvents: "none",
                },
                "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: -100,
                    right: -100,
                    width: 500,
                    height: 500,
                    background: "radial-gradient(circle, rgba(233,69,96,0.2) 0%, transparent 70%)",
                    filter: "blur(50px)",
                    pointerEvents: "none",
                },
            }}
        >
            <Box
                sx={{
                    maxWidth: "1440px",
                    margin: "0 auto",
                    mb: 0,
                    mt: 5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    position: "relative",
                    zIndex: 2,
                }}
            >
                <Box>
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 900,
                            color: "#fff",
                            fontSize: { xs: "1.8rem", md: "2.5rem" },
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            letterSpacing: "-1px",
                            textShadow: "0 4px 10px rgba(0,0,0,0.3)",
                        }}
                    >
                        Lanzamientos
                        <Box
                            component="span"
                            sx={{
                                fontSize: "0.8em",
                                animation: "float 3s ease-in-out infinite",
                                "@keyframes float": {
                                    "0%, 100%": { transform: "translateY(0)" },
                                    "50%": { transform: "translateY(-10px)" },
                                }
                            }}
                        >

                        </Box>
                    </Typography>
                    <Typography
                        sx={{
                            color: "rgba(255,255,255,0.7)",
                            mt: 0.5,
                            fontSize: "1.1rem",
                            fontWeight: 500,
                        }}
                    >
                        Descubre las últimas novedades
                    </Typography>
                </Box>

                <Link to="/lanzamientos" style={{ textDecoration: 'none', zIndex: 10, position: 'relative' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            padding: '10px 20px',
                            borderRadius: '50px',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: "0.95rem",
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                transform: 'translateX(5px)',
                                borderColor: 'rgba(255,255,255,0.4)',
                            }
                        }}
                    >
                        Ver todo <ArrowForwardRounded fontSize="small" />
                    </Box>
                </Link>
            </Box>

            {/* Scroll Container */}
            <Box
                ref={scrollRef}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                sx={{
                    display: "flex",
                    gap: 4,
                    overflowX: "auto", // Allow native scroll interaction if needed, hidden scrollbar via CSS
                    paddingBottom: 8, // Increased padding for hover effects
                    paddingTop: 4,
                    // scrollBehavior: "smooth", // Removed to prevent conflict with JS animation
                    "&::-webkit-scrollbar": { display: "none" },
                    scrollbarWidth: "none",
                    position: "relative",
                    zIndex: 2,
                }}
            >
                {boxProducts.map((product, index) => {
                    if (!product) return (
                        // Skeleton
                        <Box
                            key={`skeleton-${index}`}
                            sx={{ minWidth: 280, maxWidth: 280, borderRadius: "20px", overflow: "hidden" }}
                        >
                            <Skeleton variant="rectangular" width={280} height={360} sx={{ bgcolor: "rgba(255,255,255,0.1)" }} />
                        </Box>
                    );

                    const imgUrl = product.img && product.img.length > 0 ? product.img[0].image : null;

                    return (
                        <Box
                            key={`${product.id}-${index}`}
                            sx={{
                                minWidth: 280,
                                maxWidth: 280,
                                position: "relative",
                                transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                                "&:hover": {
                                    transform: "scale(1.05) translateY(-10px)",
                                    zIndex: 10,
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    height: 380,
                                    backgroundColor: "rgba(255,255,255,0.05)",
                                    backdropFilter: "blur(15px)",
                                    borderRadius: "24px",
                                    overflow: "hidden",
                                    border: "1px solid rgba(255,255,255,0.1)",
                                    position: "relative",
                                    display: "flex",
                                    flexDirection: "column",
                                    transition: "all 0.3s ease",
                                    "&:hover": {
                                        backgroundColor: "rgba(255,255,255,0.1)",
                                        borderColor: "rgba(255,255,255,0.4)",
                                        boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
                                    }
                                }}
                            >
                                {/* Global Product Link Overlay */}
                                <Link
                                    to={autenticado ? `/producto/${product.id}` : `/product/${product.id}`}
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        zIndex: 1
                                    }}
                                />

                                {/* Image Container */}
                                <Box
                                    sx={{
                                        height: 260,
                                        width: "100%",
                                        overflow: "hidden",
                                        position: "relative",
                                        background: "#fff",
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={imgUrl}
                                        alt={product.name}
                                        sx={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "contain",
                                            p: 2,
                                            transition: "transform 0.5s ease",
                                            "&:hover": { transform: "scale(1.1)" }
                                        }}
                                    />

                                    {/* New Badge */}
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            top: 15,
                                            left: 15,
                                            background: "rgba(255,255,255,0.9)",
                                            color: "#D81B60",
                                            fontSize: "0.7rem",
                                            fontWeight: 800,
                                            padding: "4px 12px",
                                            borderRadius: "30px",
                                            boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
                                        }}
                                    >
                                        NUEVO
                                    </Box>
                                </Box>

                                {/* Info */}
                                <Box sx={{ p: 2.5, flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                                    <Box>
                                        <Typography
                                            sx={{
                                                color: "rgba(255,255,255,0.5)",
                                                fontSize: "0.75rem",
                                                fontWeight: 600,
                                                mb: 0.5,
                                                textTransform: "uppercase",
                                                letterSpacing: "0.5px",
                                                position: "relative",
                                                zIndex: 2
                                            }}
                                        >
                                            {product.brand ? (
                                                <Link
                                                    to={`/marca/${product.brand.slug}`}
                                                    style={{ color: "inherit", textDecoration: "none" }}
                                                >
                                                    {product.brand.name}
                                                </Link>
                                            ) : "MerryColor"}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                color: "#fff",
                                                fontWeight: 700,
                                                fontSize: "1rem",
                                                lineHeight: 1.3,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                display: "-webkit-box",
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: "vertical",
                                            }}
                                        >
                                            {product.name}
                                        </Typography>
                                    </Box>

                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            mt: 2,
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                color: "#FFC1E3",
                                                fontWeight: 800,
                                                fontSize: "1.2rem",
                                            }}
                                        >
                                            {FormatCurrency(product.price || 0)}
                                        </Typography>
                                        <Box
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: "50%",
                                                background: "rgba(255,255,255,0.1)",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center"
                                            }}
                                        >
                                            <ArrowForwardRounded sx={{ color: "#fff", fontSize: 18 }} />
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box >
    );
};

export default LaunchesBanner;
